/**
 * Chrome specific to the donut and pie chart: the slice legend, the slice data
 * table, and the slice tooltip.
 *
 * These are shaped around part-to-whole data (every row carries a share of a
 * total), which is why they live beside the chart rather than in the shared
 * ChartStates module. Cartesian charts get their own equivalents.
 */

import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../ui/utils";
import { formatShare } from "../chartUtils";
import { LegendPager } from "../LegendPager";
import type { ChartSlice } from "./donutUtils";

/* ------------------------------------------------------------------ */
/* Legend                                                              */
/* ------------------------------------------------------------------ */

interface SliceLegendProps {
  slices: ChartSlice[];
  total: number;
  unit: string;
  activeId: string | null;
  hiddenIds: Set<string>;
  position: "right" | "bottom";
  interactive: boolean;
  /**
   * Draw each row's share beside its value. Off for a legend in a narrow column, where the extra
   * number wraps and pulls the rows out of line — the shares are already on the chart itself.
   */
  /** Draw each row's value. Off by default: the legend is a key, not a table. */
  showValue?: boolean;
  showShare?: boolean;
  valueFormatter: (value: number) => string;
  onActivate: (id: string | null) => void;
  onToggle: (id: string) => void;
}

/**
 * Identity channel for the chart. Always rendered for two or more slices, so
 * meaning never rests on colour alone. Items are buttons: hover or focus
 * highlights the matching slice, activation hides or restores it.
 */
export function SliceLegend({
  slices,
  total,
  unit,
  activeId,
  hiddenIds,
  position,
  interactive,
  showValue = false,
  showShare = false,
  valueFormatter,
  onActivate,
  onToggle,
}: SliceLegendProps) {
  // Beside the ring the legend is a column and has the height to grow into.
  // Above or below it is a rail: one row that pages, so however many slices
  // there are the plot below never shifts down.
  const asColumn = position === "right";

  const rows = (
    <ul
      className={cn(
        "min-w-0 gap-x-4 gap-y-1.5",
        asColumn
          ? "flex w-full min-w-[9rem] flex-1 flex-col justify-center"
          : "flex w-max flex-row flex-nowrap items-center",
      )}
    >
      {slices.map((slice) => {
        const hidden = hiddenIds.has(slice.id);
        const dimmed = !hidden && activeId != null && activeId !== slice.id;
        return (
          <li key={slice.id} className={asColumn ? "min-w-0" : "shrink-0"}>
            <button
              type="button"
              disabled={!interactive}
              onMouseEnter={() => onActivate(slice.id)}
              onMouseLeave={() => onActivate(null)}
              onFocus={() => onActivate(slice.id)}
              onBlur={() => onActivate(null)}
              onClick={() => onToggle(slice.id)}
              aria-pressed={interactive ? !hidden : undefined}
              title={
                interactive
                  ? hidden
                    ? `Show ${slice.label}`
                    : `Hide ${slice.label}`
                  : undefined
              }
              className={cn(
                "flex w-full min-w-0 items-start gap-2 rounded-md px-1 py-1 text-left transition-opacity",
                interactive &&
                  "cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                !interactive && "cursor-default",
                hidden && "opacity-40",
                dimmed && "opacity-55",
              )}
            >
              {/* A rounded square, not a dot: every other legend in the
                  library uses one, and a slice legend sitting beside a bar
                  legend with a different swatch shape reads as a different
                  kind of control. The tinted ring is the same neon treatment
                  the breakdown legend's swatch carries. */}
              <span
                aria-hidden="true"
                className={cn(
                  "mt-[3px] size-2.5 shrink-0 rounded-[3px]",
                  hidden && "ring-1 ring-inset ring-border",
                )}
                style={{
                  backgroundColor: hidden ? "transparent" : slice.color,
                  boxShadow: hidden
                    ? undefined
                    : `0 0 0 2px color-mix(in srgb, ${slice.color} 20%, transparent)`,
                }}
              />
              {/* As a column, min-w keeps the label from being squeezed to a
                  sliver and broken one character per line. On the rail it must
                  not wrap at all, or the row grows a second line. */}
              <span
                className={cn(
                  "text-[11px] font-medium leading-snug text-muted-foreground",
                  asColumn ? "min-w-[4.5rem] flex-1 break-words" : "whitespace-nowrap",
                )}
              >
                {slice.label}
              </span>
              {showValue && (
                <span className="shrink-0 text-[11px] font-bold leading-snug tabular-nums text-foreground">
                  {valueFormatter(slice.value)}
                  {unit}
                </span>
              )}
              {/* A hidden slice is out of the total, so quoting a share of it
                  would be wrong. Keep the column so rows stay aligned. */}
              {showShare && (
                <span className="shrink-0 text-right text-[11px] font-medium leading-snug tabular-nums text-muted-foreground">
                  {hidden ? "" : formatShare(slice.value, total)}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return asColumn ? rows : <LegendPager className="w-full">{rows}</LegendPager>;
}

/* ------------------------------------------------------------------ */
/* Data table view                                                     */
/* ------------------------------------------------------------------ */

interface SliceTableProps {
  slices: ChartSlice[];
  total: number;
  unit: string;
  categoryHeader: string;
  open: boolean;
  valueFormatter: (value: number) => string;
  onToggle: () => void;
}

/**
 * Text equivalent of the chart. Required relief for the light-mode fills that
 * fall below 3:1 against the card, and the fallback for anyone who cannot read
 * the slice colours at all.
 */
export function SliceTable({
  slices,
  total,
  unit,
  categoryHeader,
  open,
  valueFormatter,
  onToggle,
}: SliceTableProps) {
  return (
    <div className="mt-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md px-1 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        {open ? "Hide data table" : "Show data table"}
      </button>

      {open && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left text-[11px]">
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="py-1.5 pr-3 font-semibold text-muted-foreground">
                  {categoryHeader}
                </th>
                <th scope="col" className="py-1.5 pr-3 text-right font-semibold text-muted-foreground">
                  Value
                </th>
                <th scope="col" className="py-1.5 text-right font-semibold text-muted-foreground">
                  Share
                </th>
              </tr>
            </thead>
            <tbody>
              {slices.map((slice) => (
                <tr key={slice.id} className="border-b border-border/50 last:border-0">
                  <th scope="row" className="py-1.5 pr-3 font-medium text-foreground">
                    <span className="flex items-start gap-2">
                      <span
                        aria-hidden="true"
                        className="mt-[3px] size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: slice.color }}
                      />
                      <span className="break-words">{slice.label}</span>
                    </span>
                  </th>
                  <td className="py-1.5 pr-3 text-right font-bold tabular-nums text-foreground">
                    {valueFormatter(slice.value)}
                    {unit}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {formatShare(slice.value, total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border">
                <th scope="row" className="py-1.5 pr-3 font-semibold text-muted-foreground">
                  Total
                </th>
                <td className="py-1.5 pr-3 text-right font-bold tabular-nums text-foreground">
                  {valueFormatter(total)}
                  {unit}
                </td>
                <td className="py-1.5 text-right tabular-nums text-muted-foreground">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tooltip                                                             */
/* ------------------------------------------------------------------ */

interface SliceTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: ChartSlice }>;
  total: number;
  unit: string;
  valueFormatter: (value: number) => string;
}

/** Hover readout. Text uses ink tokens; the colour rides the swatch beside it. */
export function SliceTooltip({
  active,
  payload,
  total,
  unit,
  valueFormatter,
}: SliceTooltipProps) {
  const slice = payload?.[0]?.payload;
  if (!active || !slice) return null;

  return (
    <div className="max-w-[220px] rounded-xl border border-border bg-card px-3 py-2 shadow-lg">
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className="mt-[4px] size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: slice.color }}
        />
        <span className="min-w-0 break-words text-xs font-bold leading-snug text-foreground">
          {slice.label}
        </span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5 pl-[18px]">
        <span className="text-sm font-bold tabular-nums text-foreground">
          {valueFormatter(slice.value)}
          {unit}
        </span>
        <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
          {formatShare(slice.value, total)} of total
        </span>
      </div>
      {slice.isOther && slice.members?.length ? (
        <p className="mt-1 break-words pl-[18px] text-[10px] leading-snug text-muted-foreground">
          Includes: {slice.members.join(", ")}
        </p>
      ) : null}
    </div>
  );
}
